import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../supabase';

/**
 * M70c (E1/E2/E8) — session lifecycle helpers shared by the Shot flow and,
 * from M68a on, the Range pages.
 */

/** The quick-session insert copies RangeSessionsPage.handleAdd's shape with
 *  Shot-flow defaults — but RETURNS the error (the original ignores it; the
 *  offline queue must see failures, E2). */
export async function createQuickSession(
  userId: string,
  input: { date: string; venueId: string | null; environment?: 'indoor' | 'outdoor' },
): Promise<{ data: { id: string } | null; error: PostgrestError | null }> {
  const { data, error } = await supabase.from('range_sessions').insert({
    user_id: userId,
    date: input.date,
    venue_id: input.venueId,
    location: '',
    environment: input.environment ?? 'outdoor',
    duration_minutes: 0,
    drill_description: '',
    notes: '',
    is_planned: false,
  }).select('id').maybeSingle();
  return { data: (data as { id: string } | null) ?? null, error };
}

/** M72a — the database now enforces "a stamped trip has no open lines" via
 *  `trg_guard_session_reconciled`. Any path that stamps `reconciled_at` can
 *  therefore be refused server-side; this recognizes that refusal so callers
 *  word it as "still has open lines" instead of leaking a raw Postgres error.
 *  The message is matched too, because 23514 is the generic check_violation
 *  every other guard on these tables also raises. */
export const CHECK_VIOLATION_SQLSTATE = '23514';

export function isOpenLinesRefusal(error: PostgrestError | null | undefined): boolean {
  return !!error
    && error.code === CHECK_VIOLATION_SQLSTATE
    && error.message.includes('close those out first');
}

/** E1 — the shared "this trip is done" stamp: `{is_planned:false,
 *  reconciled_at:now()}`. Callers gate on ZERO open lines of BOTH kinds
 *  (ammo manifest + packed mags); this helper just writes. M68a adopts it.
 *
 *  M72a: the write can now be REFUSED by the server when that gate was wrong.
 *  Callers reaching this directly should run the error through
 *  `isOpenLinesRefusal` before showing it. */
export async function markSessionComplete(sessionId: string): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('range_sessions')
    .update({ is_planned: false, reconciled_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', sessionId);
  return { error };
}

/** M68a (EE2) — the guarded stamp. `markSessionComplete` writes unconditionally
 *  by contract, and its callers decide eligibility from list-page state that can
 *  be STALE or have failed to load. Re-probing immediately before the write is
 *  what stops a failed open-line fetch from stamping `reconciled_at` on a trip
 *  that still has dangling manifest/mag lines (the M67 ledger keys on that
 *  field).
 *
 *  M72a closed the residual race this used to accept: a line landing between
 *  probe and write is now refused by `trg_guard_session_reconciled`, so it
 *  comes back as `open-lines` rather than a stamped-but-dangling trip. The
 *  client probe stays because it produces the counts the UI words, and it
 *  fails without a round trip to a raising trigger.
 *
 *  Returns a typed refusal instead of throwing so callers can word it. */
export type CompleteRefusal = 'open-lines' | 'probe-failed' | 'write-failed';

export async function completeSessionSafely(
  sessionId: string,
): Promise<{ ok: boolean; refusal?: CompleteRefusal; openAmmo?: number; openMags?: number; error?: PostgrestError | null }> {
  const { openAmmo, openMags, error: probeError } = await fetchOpenLineCounts(sessionId);
  if (probeError) return { ok: false, refusal: 'probe-failed', error: probeError };
  if (openAmmo > 0 || openMags > 0) {
    return { ok: false, refusal: 'open-lines', openAmmo, openMags };
  }
  const { error } = await markSessionComplete(sessionId);
  if (error) {
    // The server enforces the same rule the probe above checks. If it refuses,
    // the probe was stale — report it as open lines, not as a failed write.
    if (isOpenLinesRefusal(error)) return { ok: false, refusal: 'open-lines', error };
    return { ok: false, refusal: 'write-failed', error };
  }
  return { ok: true };
}

/** M68a — Undo for the snackbar (DD5). Puts the trip back the way it was: a
 *  zero-line stale plan is the only thing that can reach "Mark complete", so
 *  restoring `is_planned` + clearing the stamp is the whole inverse. */
export async function revertSessionComplete(sessionId: string): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('range_sessions')
    .update({ is_planned: true, reconciled_at: null, updated_at: new Date().toISOString() })
    .eq('id', sessionId);
  return { error };
}

/** M72c — the exit the D6 magazine guard tells you to take.
 *
 *  Clears the close-out stamp and NOTHING else. Deliberately not
 *  `revertSessionComplete`, which also sets `is_planned = true`: that one undoes
 *  a "Mark complete" on a stale *plan*, where restoring the plan is the whole
 *  inverse. Reopening a trip that actually happened must not re-file it as
 *  something you intend to do.
 *
 *  Safe against the M72a invariant by construction — its trigger fires on
 *  `OLD.reconciled_at IS NULL AND NEW.reconciled_at IS NOT NULL`, so clearing
 *  the stamp is never blocked. Re-deriving happens on the next close-out. */
export async function reopenSession(sessionId: string): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('range_sessions')
    .update({ reconciled_at: null, updated_at: new Date().toISOString() })
    .eq('id', sessionId);
  return { error };
}

/** E8 — deduct-now and amend lanes clear `is_planned` client-side (parity
 *  with the ammo lane's server-side clear; the mag RPC never touches it).
 *  A Shot converts a planned trip into the real one. */
export async function clearSessionPlanned(sessionId: string): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('range_sessions')
    .update({ is_planned: false, updated_at: new Date().toISOString() })
    .eq('id', sessionId)
    .eq('is_planned', true);
  return { error };
}

/** Both-kinds open-line probe for the "Done for the day?" gate (E1). */
export async function fetchOpenLineCounts(
  sessionId: string,
): Promise<{ openAmmo: number; openMags: number; error: PostgrestError | null }> {
  const [ammoRes, magRes] = await Promise.all([
    supabase.from('range_session_ammo_manifest')
      .select('id', { count: 'exact', head: true })
      .eq('range_session_id', sessionId)
      .is('reconciled_at', null),
    supabase.from('range_session_magazines')
      .select('id', { count: 'exact', head: true })
      .eq('range_session_id', sessionId)
      .is('reconciled_at', null),
  ]);
  return {
    openAmmo: ammoRes.count ?? 0,
    openMags: magRes.count ?? 0,
    error: ammoRes.error ?? magRes.error,
  };
}

/** "Done for the day?" eligibility (E1): the quick-log session carries NO ammo
 *  manifest lines at all, no open mag lines, and isn't already stamped. */
export async function fetchDoneForDayGate(
  sessionId: string,
): Promise<{ eligible: boolean; error: PostgrestError | null }> {
  const [manifestRes, magRes, sessRes] = await Promise.all([
    supabase.from('range_session_ammo_manifest')
      .select('id', { count: 'exact', head: true })
      .eq('range_session_id', sessionId),
    supabase.from('range_session_magazines')
      .select('id', { count: 'exact', head: true })
      .eq('range_session_id', sessionId)
      .is('reconciled_at', null),
    supabase.from('range_sessions')
      .select('reconciled_at').eq('id', sessionId).maybeSingle(),
  ]);
  const error = manifestRes.error ?? magRes.error ?? sessRes.error;
  const eligible = !error
    && (manifestRes.count ?? 0) === 0
    && (magRes.count ?? 0) === 0
    && !!sessRes.data
    && !(sessRes.data as { reconciled_at: string | null }).reconciled_at;
  return { eligible, error };
}
